// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Badge, Box, Button, Collapse, Flex, Group, List, Paper, Space, Sx, Text, ThemeIcon } from '@mantine/core';
import { IconCheck, IconChevronDown, IconCircleCheck } from '@tabler/icons';
import { FC, useContext, useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import { A11y, Navigation, Pagination } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { DataS } from '../../../Service/DataS';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { BillingInterval, BillingPlan } from '../../../Types/Billing';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import Plan from './Plan';

const mobileCardSwiperSx: Sx = (theme) => ({
  marginLeft: -4,
  marginRight: -4,
  '.swiper': {
    paddingLeft: 4,
    paddingRight: 4,
    paddingBottom: 30,
    overflow: 'visible',
  },
  '.swiper-wrapper': {
    alignItems: 'stretch',
  },
  '.swiper-slide': {
    height: 'auto',
    display: 'flex',
  },
  '.swiper-slide > .mantine-Indicator-root': {
    width: '100%',
    maxWidth: '100%',
  },
  '.swiper-pagination': {
    bottom: 0,
  },
  '.swiper-button-prev, .swiper-button-next': {
    width: 32,
    height: 32,
    marginTop: -28,
    borderRadius: 32,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.white,
    boxShadow: theme.colorScheme === 'dark' ? '0 5px 16px rgba(0, 0, 0, 0.3)' : '0 5px 16px rgba(29, 81, 145, 0.16)',
    color: '#FE8047',
    '&::after': {
      fontSize: 13,
      fontWeight: 900,
    },
  },
  '.swiper-button-prev': {
    left: 0,
  },
  '.swiper-button-next': {
    right: 0,
  },
  '.swiper-button-disabled': {
    opacity: 0.28,
  },
  '.swiper-pagination-bullet': {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[4],
    opacity: 1,
  },
  '.swiper-pagination-bullet-active': {
    backgroundColor: '#FE8047',
  },
});

const visiblePrimaryPlanNames = ['Essentials', 'Standard'];
const STARTER_VISIBLE_FEATURES = 3;

const StarterPlanTeaser: FC<{ billingPlan: BillingPlan; active: boolean; sm: boolean }> = ({
  billingPlan,
  active,
  sm,
}) => {
  const [expanded, setExpanded] = useState(false);
  const visibleFeatures = billingPlan.features.slice(0, STARTER_VISIBLE_FEATURES);
  const additionalFeatures = billingPlan.features.slice(STARTER_VISIBLE_FEATURES);

  return (
    <Paper
      withBorder
      radius="md"
      p={sm ? 'sm' : 'md'}
      mt="xl"
      mx="auto"
      sx={(theme) => ({
        maxWidth: 760,
        borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
      })}
    >
      <Group
        position="apart"
        align="center"
        spacing="md"
        noWrap={!sm}
        sx={{
          '@media (max-width: 576px)': {
            alignItems: 'stretch',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Group spacing={7} mb={5} sx={{ rowGap: 5 }}>
            <Badge size="sm" radius="sm" color="gray" variant="light" sx={{ textTransform: 'none' }}>
              Starter
            </Badge>
            <Badge size="sm" radius="sm" color="green" variant="light" sx={{ textTransform: 'none' }}>
              dauerhaft kostenlos
            </Badge>
            {active && (
              <Badge
                size="sm"
                radius="sm"
                color="orange"
                variant="light"
                leftSection={<IconCircleCheck size={12} stroke={2.6} />}
                sx={{ textTransform: 'none' }}
                styles={{ leftSection: { display: 'flex', alignItems: 'center' } }}
              >
                Aktiv
              </Badge>
            )}
          </Group>
          <Text weight={900} size={sm ? 'sm' : 'md'} sx={{ lineHeight: 1.25 }}>
            Nur die kostenlose Basis benötigt?
          </Text>
          <Text mt={4} size="xs" color="dimmed" sx={{ maxWidth: 560, lineHeight: 1.5 }}>
            {billingPlan.description} Die bezahlten Pakete bleiben die beste Wahl, wenn bookitup aktiv Zeit in Deinem
            Buchungsprozess sparen soll.
          </Text>
        </Box>

        <Button
          type="button"
          variant="subtle"
          color="gray"
          size="xs"
          compact
          fullWidth={sm}
          aria-expanded={expanded}
          rightIcon={
            <IconChevronDown
              size={15}
              stroke={2.4}
              style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 160ms ease' }}
            />
          }
          onClick={() => setExpanded((current) => !current)}
          sx={{ flex: '0 0 auto' }}
        >
          {expanded ? 'Weniger' : 'Details'}
        </Button>
      </Group>

      <Group mt="sm" spacing="xs" sx={{ rowGap: 7 }}>
        {visibleFeatures.map((feature) => (
          <Group key={feature} spacing={5} noWrap>
            <ThemeIcon size={18} radius="xl" color="gray" variant="light">
              <IconCheck size={11} stroke={3} />
            </ThemeIcon>
            <Text size="xs" color="dimmed" weight={700}>
              {feature}
            </Text>
          </Group>
        ))}
      </Group>

      <Collapse in={expanded}>
        <List mt="sm" spacing={6} size="xs" icon={<IconCheck size={13} stroke={3} color="#868e96" />}>
          {additionalFeatures.map((feature) => (
            <List.Item key={feature}>{feature}</List.Item>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

const AvailablePlans: FC<{ billingInterval: BillingInterval }> = ({ billingInterval }) => {
  const { sm } = useContext(WindowContext);
  const jwtClaims = useJwtClaims();
  const plans = DataS.billingPlans;
  const primaryPlans = plans.filter((plan) => visiblePrimaryPlanNames.includes(plan.name));
  const starterPlan = plans.find((plan) => plan.free);

  return (
    <div>
      {sm ? (
        <Box sx={mobileCardSwiperSx}>
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            navigation
            pagination={{ clickable: true, dynamicBullets: primaryPlans.length > 4 }}
            slidesPerView={1.08}
            spaceBetween={16}
            centeredSlides
            grabCursor
          >
            {primaryPlans.map((plan) => (
              <SwiperSlide key={plan.name}>
                <Plan billingPlan={plan} billingInterval={billingInterval} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      ) : (
        <Flex gap="lg" wrap="wrap" justify="center" align="stretch">
          {primaryPlans.map((plan) => (
            <Plan key={plan.name} billingPlan={plan} billingInterval={billingInterval} />
          ))}
        </Flex>
      )}
      {starterPlan && (
        <StarterPlanTeaser billingPlan={starterPlan} active={jwtClaims.licence === starterPlan.licence} sm={sm} />
      )}
      <Space h="md" />
    </div>
  );
};

export default AvailablePlans;
